/* Cliente para las Cloud Functions de podios-stories.
 *
 *   - fetchEvents       → GET /api/events (scraping de la competición).
 *   - fetchCampeonatos  → GET /api/campeonatos (desplegable RFEA Live).
 *   - renderAtletaVideo → POST /api/render-atleta-video (Pillow + ffmpeg).
 * La imagen del podio/ficha se genera en cliente (html2canvas), por eso ya
 * no hay render de imagen ni de vídeo de podio en servidor.
 * Todas pasan por el rewrite de Hosting (same-origin en prod; proxy de Vite
 * en dev — ver vite.config.ts).
 */

import { auth } from '@/lib/firebase';

export type Fuente = 'conersys' | 'fam' | 'rfea.me' | 'rfea.es' | 'ea' | 'wa';

/** Un intento de concurso. `height` solo se rellena en pruebas por listones
 *  (altura/pértiga); en long/triple/lanzamientos va vacío y `mark` es la
 *  distancia o "X". */
export interface Intento {
  mark: string;
  height?: string;
}

/** Una disciplina de una prueba combinada (heptatlón/decatlón) para el
 *  desglose del protagonista: nombre, marca, puntos de ESA prueba y viento. */
export interface PruebaCombinada {
  disciplina: string;
  marca: string;
  puntos: string;
  viento?: string;
}

export interface PodioRunner {
  puesto: string;
  /** Nombre para mostrar (puede ser corto). */
  nombre: string;
  /** Nombre completo para cruce contra BD (si difiere de `nombre`). */
  atleta?: string | null;
  club: string;
  /** Club real de la BD (si el front decide mostrarlo en vez de `club`). */
  club_real?: string;
  marca: string;
  viento?: string;
  pais?: string | null;
  /** Fuera de concurso (IsGuest del RES; chip "FC" en rfealive): no opta a
   *  medalla del campeonato. undefined si el backend aún no lo manda. */
  esGuest?: boolean;
  /** Podio oficial de la fuente ("1"|"2"|"3", Medal del RES, ya sin FC). */
  medalla?: string | null;
  fechaNac?: string | null;
  /** Integrantes de un relevo (nombre + apellidos, en orden de posta). null en
   *  pruebas individuales. La tarjeta los pinta bajo el club como "P. Rojo - …". */
  integrantes?: { nombre: string; apellidos: string }[] | null;
  /** Id estable del atleta (solo rfea.es). Empareja con el detalle de
   *  intentos (RES<Key>) que sirve /api/event-detail. */
  athId?: string | null;
  /** Clasificación a la siguiente ronda, solo en rondas previas (rfea.es):
   *  "Q" = pasa por plazas (directo), "q" = por tiempos (la fuente la
   *  rellena cuando TODAS las series están en Oficial). null en finales. */
  quali?: string | null;
  /** Serie de intentos de un concurso. Cada intento trae `mark` (distancia
   *  "44.07" / "X" nulo en long/triple/lanzamientos; o símbolos "O"/"XO"/"XXX"/
   *  "-"/"r" en altura/pértiga) y `height` (altura del listón, solo vertical).
   *  Lo rellena la página de atleta bajo demanda (fetchEventDetail). Solo
   *  rfea.es y solo cuando la prueba es un concurso. */
  intentos?: Intento[];
  /** Desglose por disciplina de una prueba combinada (hept/decatlón). Lo
   *  rellena la página de atleta bajo demanda para el protagonista. */
  combinada?: PruebaCombinada[];
  /** Dorsal (Bib) y categoría (U20M, SM…). Solo el detalle RES de rfea.es
   *  los trae; los usa la tarjeta de Telegram del modo admin. */
  dorsal?: string | null;
  categoria?: string | null;
  /** Píldoras a pintar en el vídeo/imagen final (PB, SB, R.Esp, etc.).
   *  Lo rellena el cliente con el cruce TS (Fase 2). */
  etiquetas?: string[];
  /** Flags crudas del cruce (RECORD_ESPANA, N1_*, ['MINIMA', label]...).
   *  A diferencia de `etiquetas` (que aplica la jerarquía "el récord engloba
   *  todo" y oculta implícitos), aquí está TODO lo conseguido — necesario
   *  para la caja "Destacados" de Home (pedido de Riki 2026-07-08). */
  flags?: (string | [string, string])[];
}

export interface Evento {
  pruebaId?: string;
  nombre: string;
  ronda: string;
  hora: string;
  fecha: string;
  estado: string;
  viento?: string;
  /** Top-3 (podio). Lo consume la herramienta admin (Editor/PodioCard). */
  podio: PodioRunner[];
  /** Clasificación COMPLETA de la prueba (todos los atletas, ordenados por
   *  puesto). La rellena el backend (functions/main.py). La usa la página
   *  pública de atleta para elegir protagonista + vecinos. El cruce de
   *  etiquetas sobre este campo se hace bajo demanda (no en fetchEvents). */
  resultados?: PodioRunner[];
  /** Key del evento (rfea.es) para bajar el detalle de intentos de un
   *  concurso vía fetchEventDetail. null/ausente en el resto de fuentes. */
  detalleKey?: string | null;
}

/** Inscripción de un atleta en una prueba (solo rfea.es, desde ALLINS).
 *  Permite la búsqueda por atleta fuera del top-3: el SCH solo publica el
 *  podio por evento, pero las inscripciones tienen a todos. `eve` casa con
 *  el 3er segmento de `Evento.detalleKey` (champ:parent:EVE:fase:unidad). */
export interface Inscrito {
  nombre: string;
  eve: string;
  athId?: string | null;
}

export interface EventsResponse {
  competicion: string;
  fecha?: string;
  sede?: string | null;
  /** Solo rfea.es; ausente en el resto de fuentes. */
  inscritos?: Inscrito[];
  fuente: Fuente;
  eventos: Evento[];
}

export interface Campeonato {
  code: string;
  titulo: string;
  fecha_inicio: string;
  fecha_fin: string;
}

export interface RenderResponse {
  /** URL del MP4/PNG generado (en Storage o data URL). */
  url: string;
  contentType: 'video/mp4' | 'image/png';
}

/* ─────────────  Implementación  ───────────── */

async function authHeader(): Promise<Record<string, string>> {
  const u = auth.currentUser;
  if (!u) throw new Error('Sin sesión activa');
  const idToken = await u.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
}

/** Scrapea + devuelve eventos.
 *  Llama a /api/events (rewrite a la Cloud Function `events`).
 *  Same-origin via Hosting → sin preflight CORS en prod. */
export async function fetchEvents(url: string): Promise<EventsResponse> {
  if (!url.trim()) throw new Error('URL vacía');
  const headers = await authHeader();
  const r = await fetch(`/api/events?url=${encodeURIComponent(url)}`, {
    method: 'GET',
    headers,
  });
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`events ${r.status}: ${body.slice(0, 240)}`);
  }
  const data = (await r.json()) as EventsResponse;
  await cruzarPodios(data); // puebla p.etiquetas (PB/SB/récord/mínima/líder)
  return data;
}

/** Ejecuta el cruce sobre todos los podios y rellena `etiquetas` en cada
 *  atleta (in-place). Si el cruce falla (datos no disponibles, etc.) los
 *  podios se devuelven sin etiquetas — nunca rompe el listado. */
async function cruzarPodios(data: EventsResponse): Promise<void> {
  try {
    const { construirIndices, analizarCompeticion } = await import('@/lib/podios/data');
    type FilaRef = { runner: PodioRunner; fila: import('@/lib/podios/data').FilaPodio };
    const refs: FilaRef[] = [];
    for (const ev of data.eventos) {
      for (const p of ev.podio) {
        refs.push({
          runner: p,
          fila: {
            nombre: p.nombre, atleta: p.atleta ?? p.nombre, pais: p.pais,
            prueba: ev.nombre, marca: p.marca, fechaNac: p.fechaNac,
            viento: p.viento, fecha: ev.fecha, lugar: data.sede ?? '',
          },
        });
      }
    }
    if (!refs.length) return;
    const idx = await construirIndices(refs.map((r) => r.fila));
    const resultados = analizarCompeticion(refs.map((r) => r.fila), idx);
    resultados.forEach((res, i) => {
      refs[i].runner.etiquetas = res.etiquetas;
      refs[i].runner.flags = res.out.flags;
    });
  } catch (e) {
    console.warn('[podios] cruce no disponible:', e);
  }
}

/** Lista de campeonatos de rfealive.es (próximos/recientes) para el
 *  desplegable. Devuelve [] si falla (nunca rompe la UI).
 *
 *  CLIENTE (2026-07-12): antes era la Cloud Function `campeonatos`; ahora se
 *  baja `ALLCH` DIRECTO del bucket S3 de conersys (público, CORS `*`) y se
 *  filtra/ordena en el navegador. Evita el arranque frío de la función (que se
 *  notaba al cargar el inicio) y descarga solo ~10 KB gzip. La función Python
 *  sigue existiendo (dormida) por si hubiera que revertir: basta restaurar este
 *  cuerpo al fetch de `/api/campeonatos`. Paridad con
 *  scraper_rfea_es.listar_campeonatos (dias_atras=30, dias_adelante=365). */
const ALLCH_URL =
  'https://conersys-live-d.s3.dualstack.eu-west-3.amazonaws.com/ALLCH';

type LangMsg = { Lang?: string; Msg?: string; ShortMsg?: string };
function langEsp(lista: LangMsg[] | undefined): string {
  if (!lista?.length) return '';
  const esp = lista.find((d) => d.Lang === 'ESP');
  return (esp?.Msg || esp?.ShortMsg || lista[0]?.Msg || '').trim();
}
function fechaFmt(iso: string | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('T')[0].split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}
/** ALLCH/RES/… son gzip CRUDO (no Content-Encoding), así que el navegador no
 *  los descomprime solo: se usa DecompressionStream('gzip'). `no-store`: S3
 *  no manda Cache-Control y la caché heurística del navegador servía listas
 *  VIEJAS (2026-07-22: el Absoluto no aparecía en el desplegable aunque ya
 *  estaba publicado en ALLCH). */
async function fetchGzipJson<T>(url: string): Promise<T | null> {
  const r = await fetch(url, { method: 'GET', cache: 'no-store' });
  if (!r.ok || !r.body) return null;
  const stream = r.body.pipeThrough(new DecompressionStream('gzip'));
  return JSON.parse(await new Response(stream).text()) as T;
}

export async function fetchCampeonatos(): Promise<Campeonato[]> {
  try {
    const datos = await fetchGzipJson<Array<Record<string, unknown>>>(ALLCH_URL);
    if (!datos) return [];
    const hoy = Date.now();
    const DIA = 86_400_000;
    const desde = hoy - 30 * DIA;
    const hasta = hoy + 365 * DIA;
    const parse = (iso: unknown): number | null => {
      if (typeof iso !== 'string') return null;
      const t = Date.parse(iso.split('T')[0]);
      return Number.isNaN(t) ? null : t;
    };
    const cands: Array<Campeonato & { _cercania: number }> = [];
    for (const d of datos) {
      if (d.ISVisible === false) continue;
      if (!d.Code) continue;
      const fi = parse(d.StartDate);
      if (fi === null) continue;
      const ff = parse(d.EndDate) ?? fi;
      if (ff < desde || fi > hasta) continue;
      const ref = fi >= hoy ? fi : ff;
      cands.push({
        code: String(d.Code),
        titulo: langEsp(d.Title as LangMsg[] | undefined),
        fecha_inicio: fechaFmt(d.StartDate as string | undefined),
        fecha_fin: fechaFmt(d.EndDate as string | undefined),
        _cercania: Math.abs(ref - hoy),
      });
    }
    cands.sort((a, b) => a._cercania - b._cercania);
    return cands.map(({ _cercania: _c, ...c }) => c);
  } catch {
    return [];
  }
}

/** Detalle de un evento rfea.es (objeto RES<Key> del S3). */
/** Un atleta en la ALINEACIÓN (lista de salida) de una prueba: calle, dorsal,
 *  nombre y club. La calle/dorsal solo están en el detalle RES de rfea.es. */
export interface AlineacionAtleta {
  athId: string;
  nombre: string;
  club: string;
  /** Calle (Lane). null/ausente en concursos sin calle. */
  calle?: string | null;
  /** Dorsal (Bib). */
  dorsal?: string | null;
}

/** Fila SIN puesto de un evento acabado (DNS/DNF/DQ/NM), tal cual la pinta el
 *  directo de RFEA. `estado` es el ShortMsg del IRM ("DNS", "DNF", "DQ"…). */
export interface NoClasificado {
  nombre: string;
  club: string;
  estado: string;
  athId?: string | null;
  dorsal?: string | null;
  categoria?: string | null;
  fechaNac?: string | null;
}

export interface EventDetail {
  /** { athId: [intentos] } de un concurso (p.ej. { "628636": [{mark:"X"},...] }). */
  intentos: Record<string, Intento[]>;
  /** { athId: [disciplinas] } de una prueba combinada (hept/decatlón). */
  combinada: Record<string, PruebaCombinada[]>;
  /** Clasificación COMPLETA de la prueba. Necesaria en rfea.es porque el SCH
   *  (y por tanto `Evento.resultados`) solo publica el top-3 por evento
   *  (verificado 2026-07-10): el campo entero SOLO está en RES<Key>. */
  clasificacion: PodioRunner[];
  /** Alineación de la unidad (athIds, con o sin puesto). En estado "Lista de
   *  Salida" la clasificación está vacía pero esto ya dice quién compite. */
  salida: string[];
  /** Alineación DETALLADA para la lista de salida: cada atleta con su calle
   *  (Lane) y dorsal (Bib). Solo está en el RES (no en el SCH). */
  alineacion: AlineacionAtleta[];
  /** Filas sin puesto de un evento acabado (DNS/DNF/DQ/NM). */
  noClasificados: NoClasificado[];
  /** Regla de calificación de la ronda (nivel documento RES): cuántos pasan
   *  por PUESTOS en cada serie (las "Q") y cuántos por TIEMPOS entre todas
   *  las series (las "q"). null en finales y concursos sin progresión.
   *  Permite calcular las "q" nada más acabar la última serie, sin esperar a
   *  que la fuente rellene `quali`. */
  qualiByRank: number | null;
  qualiByTime: number | null;
  /** MARCHA: avisos del juez por atleta ('~' pérdida de contacto, '>'
   *  rodilla doblada, 'p' pit lane), p.ej. { "240983": ">>p" }. Por athId
   *  y por dorsal (los DQ/DNF de noClasificados no llevan athId). */
  avisos?: Record<string, string>;
  avisosDorsal?: Record<string, string>;
  /** Récords/mejores marcas batidos por athId, colapsados y en orden de
   *  importancia: { "240983": ["LE","MMP"] }. Stickers de la tarjeta. */
  records?: Record<string, string[]>;
}

/** Detalle de una prueba (solo rfea.es): intentos de concurso + clasificación
 *  completa. La página de atleta lo pide BAJO DEMANDA con la `detalleKey` de
 *  la prueba seleccionada. Devuelve vacíos si no hay detalle o falla (nunca
 *  rompe). */
export async function fetchEventDetail(key: string): Promise<EventDetail> {
  const vacio: EventDetail = {
    intentos: {}, combinada: {}, clasificacion: [], salida: [], alineacion: [],
    noClasificados: [], qualiByRank: null, qualiByTime: null,
  };
  if (!key.trim()) return vacio;
  try {
    const headers = await authHeader();
    const r = await fetch(`/api/event-detail?key=${encodeURIComponent(key)}`, { method: 'GET', headers });
    if (!r.ok) return vacio;
    const data = (await r.json()) as Partial<EventDetail>;
    return {
      intentos: data.intentos ?? {},
      combinada: data.combinada ?? {},
      clasificacion: data.clasificacion ?? [],
      salida: data.salida ?? [],
      alineacion: data.alineacion ?? [],
      noClasificados: data.noClasificados ?? [],
      qualiByRank: data.qualiByRank ?? null,
      qualiByTime: data.qualiByTime ?? null,
      avisos: data.avisos ?? {},
      avisosDorsal: data.avisosDorsal ?? {},
      records: data.records ?? {},
    };
  } catch {
    return vacio;
  }
}

/* ─────────────  Publicación en Telegram (modo admin)  ───────────── */

export interface PublishTelegramRequest {
  /** Tarjeta de la serie ya rasterizada (dataURL PNG, html2canvas). */
  photo?: string;
  /** Pie de la foto (máx 1024 chars, Bot API). */
  caption?: string;
  /** Mensaje de texto (solo si no hay photo). */
  text?: string;
  /** Chat/canal destino; si falta, el backend usa TELEGRAM_CHAT_ID. */
  chatId?: string;
}

/** Publica una tarjeta/mensaje en Telegram vía la Cloud Function
 *  `publish_telegram` (auth ESTRICTA de whitelist — escribe en un canal). */
export async function publishTelegram(reqBody: PublishTelegramRequest): Promise<{ ok: boolean; messageId?: number }> {
  const headers = await authHeader();
  const r = await fetch('/api/publish-telegram', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(reqBody),
  });
  const body = await r.text();
  if (!r.ok) {
    let msg = body.slice(0, 240);
    try {
      const j = JSON.parse(body) as { error?: string };
      if (j.error) msg = j.error;
    } catch { /* texto plano */ }
    throw new Error(`publish-telegram ${r.status}: ${msg}`);
  }
  return JSON.parse(body) as { ok: boolean; messageId?: number };
}

/* ─────────────  Publicación en WhatsApp (Whapi.cloud)  ───────────── */

export interface PublishWhatsappRequest {
  /** Tarjeta de la serie ya rasterizada (dataURL PNG, html2canvas). */
  photo?: string;
  /** Pie de la foto (recortado a 1024 en el backend, por paridad). */
  caption?: string;
  /** Mensaje de texto PLANO (WhatsApp no acepta el HTML de Telegram). */
  text?: string;
  /** Destino (teléfono, grupo o canal); si falta, el backend usa WHAPI_TO. */
  to?: string;
  /** Marca la publicación en telegramDirecto/state para que el AUTOMÁTICO
   *  no la repita (serie: detalleKey; clasificados: "nombre|base ronda"). */
  stateKey?: string;
  stateKind?: 'publicados' | 'clasificados';
}

/** Publica en WhatsApp la(s) tarjeta(s) de FINAL con foto (diseño v4,
 *  2026-08-06) de una final del Mundial WA, renderizadas EN EL SERVIDOR con
 *  el mismo Pillow del automático (nada de duplicar el diseño aquí). Una
 *  tarjeta por español CON foto subida en /fotos-finales; el backend
 *  responde 409 si ninguno la tiene (usa entonces el botón clásico). */
export async function publishFinalWA(reqBody: { key: string; marcar?: boolean }): Promise<string> {
  const headers = await authHeader();
  const r = await fetch('/api/publish-final-wa', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(reqBody),
  });
  const body = await r.text();
  if (!r.ok) throw new Error(body.slice(0, 240));
  return body;
}

/** Publica una tarjeta/mensaje en WhatsApp vía la Cloud Function
 *  `publish_whatsapp` (auth ESTRICTA de whitelist — escribe en un chat). */
export async function publishWhatsapp(reqBody: PublishWhatsappRequest): Promise<{ ok: boolean; messageId?: string }> {
  const headers = await authHeader();
  const r = await fetch('/api/publish-whatsapp', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(reqBody),
  });
  const body = await r.text();
  if (!r.ok) {
    let msg = body.slice(0, 240);
    try {
      const j = JSON.parse(body) as { error?: string };
      if (j.error) msg = j.error;
    } catch { /* texto plano */ }
    throw new Error(`publish-whatsapp ${r.status}: ${msg}`);
  }
  return JSON.parse(body) as { ok: boolean; messageId?: string };
}

/** URL de la competición rfealive.es a partir del código del desplegable. */
export function urlDeCampeonato(code: string): string {
  return `https://rfealive.es/sch/${code}`;
}

/** Una capa que aparece sobre la base (fade-in + slide desde su lado). */
export interface AtletaVideoLayer {
  dataUrl: string;
  /** Lado por el que entra; null = solo fade. */
  side?: 'left' | 'right' | null;
}

export interface AtletaVideoRequest {
  /** Foto base (ya recortada) como dataURL. */
  baseDataUrl: string;
  /** Capas transparentes (datos, logo) que entran sobre la base. */
  layers: AtletaVideoLayer[];
  w: number;
  h: number;
  /** Duración en segundos. */
  dur?: number;
}

/** Genera el vídeo de ficha de atleta (zoom sobre la base + fade-in/slide de
 *  las capas) en la Cloud Function `render_atleta_video`. */
export async function renderAtletaVideo(req: AtletaVideoRequest): Promise<RenderResponse> {
  if (!req.baseDataUrl || !req.layers?.length) throw new Error('Faltan imágenes');
  const headers = await authHeader();
  const r = await fetch('/api/render-atleta-video', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  if (!r.ok) {
    const body = await r.text();
    throw new Error(`render-atleta-video ${r.status}: ${body.slice(0, 240)}`);
  }
  return (await r.json()) as RenderResponse;
}

/** Publica en INSTAGRAM vía la Cloud Function `publish_instagram` (webhook
 *  de Make): 1 foto = post normal, 2-10 = carrusel (router en Make). El
 *  backend convierte a JPEG y sube a Storage porque IG exige URL pública. */
export async function publishInstagram(reqBody: { photos: string[]; caption?: string }): Promise<{ ok: boolean; imageUrl?: string }> {
  const headers = await authHeader();
  const r = await fetch('/api/publish-instagram', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(reqBody),
  });
  if (!r.ok) {
    const msg = await r.text();
    throw new Error(`publish-instagram ${r.status}: ${msg.slice(0, 240)}`);
  }
  return (await r.json()) as { ok: boolean; imageUrl?: string };
}

/** Publicación PARCIAL forzada (concurso por rondas/listón, combinada por
 *  corte) vía `auto_directo_run`: usa el MISMO render Pillow del motor
 *  automático, así el botón del /admin prueba exactamente lo que publicará
 *  el tick del directo. Devuelve el resumen de texto del backend. */
export async function publishTelegramParcial(body: {
  url: string;
  key: string;
  corte?: number;
  chatId?: string;
  /** Canal de destino del parcial (default telegram en el backend). */
  canal?: 'telegram' | 'whatsapp';
}): Promise<string> {
  const headers = await authHeader();
  const r = await fetch('/api/auto-directo-run', {
    method: 'POST',
    headers: { ...headers, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const texto = await r.text();
  if (!r.ok) throw new Error(`auto-directo-run ${r.status}: ${texto.slice(0, 240)}`);
  return texto;
}

/** Convierte un texto a slug seguro para nombre de fichero. */
export function slugify(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'podio';
}

/** Descarga forzada con nombre propio. La URL de Storage se sirve inline
 *  y cross-origin, así que el atributo download de un <a> no basta: hay
 *  que traer el blob y disparar la descarga desde un object URL. */
export async function descargarArchivo(url: string, filename: string): Promise<void> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`descarga ${r.status}`);
  const blob = await r.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objUrl);
}
